import type { ClientEnvironment } from '@polyai/common/constants/global.constants';

import ENDPOINTS from 'api/endpoints';
import { doDelete, doGet, doPatch, doPost } from 'api/request';
import { LOGGER_TYPE } from 'api/types';
import { generateNextPath } from 'lib/routeHelper';
import type {
  AvailableAreaCodesResponse,
  CountryCode,
  PhoneNumberInAllEnvironmentsResponse,
  PhoneNumberResponse,
} from './types';

export const getPhoneNumber = (
  accountId: string,
  projectId: string,
  clientEnv?: ClientEnvironment,
) => {
  return doGet<PhoneNumberResponse>(
    generateNextPath(ENDPOINTS.PHONE_NUMBERS, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    {
      params: { client_env: clientEnv },
    },
  );
};

export const getPhoneNumbersInAllEnvironments = (
  accountId: string,
  projectId: string,
) => {
  return doGet<PhoneNumberInAllEnvironmentsResponse>(
    generateNextPath(`${ENDPOINTS.PHONE_NUMBERS}/list`, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
  );
};

export const getAvailableAreaCodes = (
  accountId: string,
  projectId: string,
  countryCode: CountryCode,
) => {
  return doGet<AvailableAreaCodesResponse>(
    generateNextPath(ENDPOINTS.PHONE_NUMBER_AREA_CODES, {
      accountId,
      projectId,
    }),
    LOGGER_TYPE.JUPITER,
    { params: { country_code: countryCode } },
  );
};

export const buyPhoneNumber = ({
  accountId,
  projectId,
  countryCode,
  clientEnv,
  variantId,
  areaCode,
}: {
  accountId: string;
  projectId: string;
  countryCode: CountryCode;
  clientEnv: ClientEnvironment;
  variantId?: string;
  areaCode?: string;
}) => {
  return doPost<PhoneNumberResponse>(
    generateNextPath(ENDPOINTS.PHONE_NUMBERS, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    {
      country_code: countryCode,
      client_env: clientEnv,
      ...(variantId && { variant_id: variantId }),
      ...(areaCode && countryCode === 'US' && { area_code: areaCode }),
    },
  );
};

export const deletePhoneNumber = ({
  accountId,
  projectId,
  phoneNumber,
  clientEnv,
  integration,
}: {
  accountId: string;
  projectId: string;
  phoneNumber: string;
  clientEnv: ClientEnvironment;
  integration?: string;
}) => {
  return doDelete(
    generateNextPath(ENDPOINTS.PHONE_NUMBERS, { accountId, projectId }),
    LOGGER_TYPE.JUPITER,
    {
      phone_number: phoneNumber,
      client_env: clientEnv,
      integration: integration,
    },
  );
};

export const reassignPhoneNumberVariant = ({
  accountId,
  projectId,
  connectorId,
  phoneNumber,
  variantId,
}: {
  accountId: string;
  projectId: string;
  variantId: string;
  connectorId?: string;
  phoneNumber?: string;
}) => {
  // TODO: Remove phoneNumber parameter once we remove the old reassign phone number variant API
  if (connectorId) {
    return doPatch<PhoneNumberResponse>(
      generateNextPath(ENDPOINTS.PHONE_NUMBER_VARIANT, {
        accountId,
        projectId,
      }),
      LOGGER_TYPE.JUPITER,
      {
        connector_id: connectorId,
        variant_id: variantId,
      },
    );
  }
  if (phoneNumber) {
    return doPatch<PhoneNumberResponse>(
      generateNextPath(ENDPOINTS.PHONE_NUMBER_VARIANT, {
        accountId,
        projectId,
      }),
      LOGGER_TYPE.JUPITER,
      {
        phone_number: phoneNumber,
        variant_id: variantId,
      },
    );
  }
  throw new Error('Either connectorId or phoneNumber must be provided');
};
